import { Fill } from "../icons/Fill"
import { Stroke } from "../icons/Stroke"
import { CSS } from "@dnd-kit/utilities"
import { selectedControlColor } from "./Toolbar"
import { useSortable } from "@dnd-kit/sortable"
import { CSSProperties, useCallback, useState } from "react"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { useEditor } from "tldraw"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined"
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined"
import { Expr } from "../../shared/Expr"
import { styled } from "@mui/material"
import { Settings } from "lucide-react"

type LayerButtonProps = {
  layerId: string
  isSelected: boolean
  isActive: boolean
  visible: boolean
  onSelected: (layerId: string) => void
  onSelectedClick: (layerId: string) => void
  onHover: (layerId: string | null) => void
  style?: CSSProperties
}
export function LayerButton2({
  layerId,
  isSelected,
  isActive,
  visible,
  onSelected,
  onSelectedClick,
  onHover,
}: LayerButtonProps) {
  const {
    setNodeRef,
    transform,
    listeners,
    attributes,
    transition,
    isDragging,
  } = useSortable({ id: `${layerId}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const [hovered, setHovered] = useState<boolean>(false)

  const editor = useEditor()
  const page = useCurrentPage()
  const pageMeta = page.meta

  const layer = pageMeta.layers[layerId]

  const handleVisibilityChange = useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation()
      const layers = {
        ...pageMeta.layers,
      }

      layers[layerId] = {
        ...layers[layerId],
        visible: Expr.lit(
          !Expr.evaluate(layers[layerId].visible, pageMeta.parameterValues),
        ),
      }

      editor.updatePage({ id: page.id, meta: { ...pageMeta, layers } })
    },
    [page.id, pageMeta],
  )

  const strokeColor =
    layer.layerOverrides.strokeColor &&
    Expr.evaluate(layer.layerOverrides.strokeColor, pageMeta.parameterValues)
  const fillColor =
    layer.layerOverrides.fillColor &&
    Expr.evaluate(layer.layerOverrides.fillColor, pageMeta.parameterValues)
  const strokeWidth =
    layer.layerOverrides.strokeWidth &&
    Expr.evaluate(layer.layerOverrides.strokeWidth, pageMeta.parameterValues)

  // fyi: dnd-kit adds a role='button' to this div
  return (
    <LayerButtonDiv
      key={layerId}
      ref={setNodeRef}
      selected={isActive}
      dragging={isDragging}
      style={style}
      {...attributes}
      {...listeners}
      // force clearing of hover state on mobile
      onMouseUp={() => onHover(null)}
      onClick={() => {
        if (isActive) {
          return onSelectedClick(layerId)
        }
        return onSelected(layerId)
      }}
      onMouseOver={() => {
        setHovered(true)
        onHover(layerId)
      }}
      onMouseOut={() => {
        setHovered(false)
        onHover(null)
      }}
    >
      {/* <IconButton size="small" onClick={handleVisibilityChange}>
        <Tooltip title="Toggle visibility">
          {visible ? (
            <VisibilityOutlined
              fontSize="inherit"
              style={{ color: "rgba(0,0,0,.8)" }}
            />
          ) : (
            <VisibilityOffOutlined
              fontSize="inherit"
              style={{ color: "rgba(0,0,0,.3)" }}
            />
          )}
        </Tooltip>
      </IconButton> */}
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div style={{ marginRight: 5 }}>{layer.name}</div>

        <PreviewCircle
          strokeColor={strokeColor}
          fillColor={fillColor}
          strokeWidth={strokeWidth}
        />
      </div>

      {isActive && (
        <div style={{ position: "absolute", left: 83, top: -2 }}>
          <IconButton size="small" onClick={() => onSelectedClick(layerId)}>
            <Settings />
          </IconButton>
        </div>
      )}
      {/* <NameContainer isSelected={isSelected || hovered}>
        {layer.name}
      </NameContainer> */}
    </LayerButtonDiv>
  )
}

type LayerButtonDivProps = {
  selected: boolean
  dragging: boolean
}

const LayerButtonDiv = styled("div")<LayerButtonDivProps>({
  position: "relative",
  background: "white",
  borderStyle: "solid",
  borderWidth: "0 5px 0 0",
  borderColor: "rgba(0, 0, 0, 0.05)",
  padding: "6px",
  // touchAction: 'none',
  touchAction: "manipulation",
  display: "flex",
  alignItems: "stretch",
  flexDirection: "column",
  zIndex: 0,
  width: 84,
  "&:hover": {
    background: "rgba(0, 50, 255, 0.05)",
  },
  "&:last-child": {
    borderRadius: "0 0 4px 0",
  },
  variants: [
    {
      props: { selected: true },
      style: {
        borderColor: selectedControlColor,
      },
    },
    {
      props: { dragging: true },
      style: {
        zIndex: 1,
      },
    },
  ],
})

type StrokeWidthNumProps = {
  visible: boolean
  children: number
}

export const StrokeWidthNum = styled("div")<StrokeWidthNumProps>({
  fontSize: 12,
  position: "absolute",
  bottom: 3,
  right: 0,
  textAlign: "center",
  color: "black",
  touchAction: "none",
  whiteSpace: "nowrap",
  variants: [
    {
      props: { visible: false },
      style: {
        opacity: 0.3,
      },
    },
  ],
})

const NameContainer = styled("div")<{ isSelected: boolean }>({
  position: "absolute",
  left: "120%", // place it to the right of the icon container
  top: "50%",
  transform: "translateY(-50%) translateX(-10px)", // initial offset
  whiteSpace: "nowrap",
  background: "white",
  padding: "2px 4px",
  borderRadius: "4px",
  fontSize: "12px",
  boxShadow: "2px 2px 5px rgba(0, 0, 0, .3)",
  transition: "transform 0.3s ease, opacity 0.3s ease",
  opacity: 0,
  pointerEvents: "none",
  variants: [
    {
      props: { isSelected: true },
      style: {
        transform: "translateY(-50%) translateX(0)", // slide in
        opacity: 1,
        pointerEvents: "auto",
      },
    },
  ],
})

function PreviewCircle({
  strokeColor,
  fillColor,
  strokeWidth,
}: {
  strokeColor?: string
  fillColor?: string
  strokeWidth?: number
}) {
  const hasStroke = strokeColor || strokeWidth !== null

  const rawWidth = strokeWidth ?? 0
  const scaledWidth = Math.min(rawWidth, 10) * 0.5 + 1
  const finalStrokeWidth = scaledWidth || (strokeColor ? 1 : 0) // fallback to 1 if color but no width
  const finalStrokeColor = strokeColor ?? "black"

  return (
    <svg width="20" height="20">
      <circle
        cx="10"
        cy="10"
        r={10 - finalStrokeWidth / 2} // ensure stroke stays inside viewbox
        fill={fillColor ?? "none"}
        stroke={hasStroke ? finalStrokeColor : "none"}
        strokeWidth={hasStroke ? finalStrokeWidth : 0}
      />
    </svg>
  )
}
